import { Fragment, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, IconButton, validationUtils, WithTooltip } from 'basic-components';
import { Clear, Info } from 'basic-components/icons';

import { PickersUtils } from '../Utils';
import Modal from './Modal';

import { Else, If, Then, When } from 'react-if';

import classNames from 'classnames';
import '../Picker.scss';

const EMPTY_ARRAY = [];
const EMPTY_OBJ = {};
export const FilePicker = ({
  disabled = false,
  size = 'normal',
  tooltipProps = EMPTY_OBJ,
  optionalText = undefined,
  onChange,
  name,
  defaultValue = undefined,
  validators = EMPTY_ARRAY,
  required = false,
  tooltipElement = 'pipeline',
  noError = false,
  fileExtensions = EMPTY_ARRAY,
  fileType = undefined,
  withVersion = true,
  withProvider = false,
  ref
}) => { 
  const [isOpen, open] = useState(false),
        [value, setValue] = useState(defaultValue),
        [isValid, setValid] = useState(true),
        [errorMessage, setErrorMessage] = useState(undefined),
        [isOverflowed, setOverflowed] = useState(false),
        
        refErrorMessage = useRef(null),

        openModal = useCallback(() => open(true), [open]),
        closeModal = useCallback(() => open(false), [open]),
        clearValue = useCallback(() => {
          if(!disabled) {
            setValue("")
           } 
           onChange("", name)
        }, [disabled, name, onChange]),
        onSubmit = useCallback((localValue, newest) => {
          const isNewest = typeof newest === "boolean" ? newest : (newest === "true")
          const newVal = {
            name: localValue.name,
            groupName: localValue.groupName,
            id: isNewest ? undefined : localValue.id,
            provider: withProvider ? localValue.provider : undefined
          }
          setValue(newVal)
          onChange(newVal, name)
        }, [name, onChange, withProvider]),
        validate = useCallback(v => validationUtils.validateNoState(v, validators, name, required, setValid, setErrorMessage), [validators, name, required])

  useEffect(() => {
    setValue(value => JSON.stringify(value) !== JSON.stringify(defaultValue) ? defaultValue : value)
  }, [defaultValue])

  useEffect(() => {
    validate(typeof value === 'object' ? value.name : value)
  }, [value, validate])

  useImperativeHandle(ref, () => ({ 
    validateForForm: () => validate(typeof value === 'object' ? value.name : value),
    isValid: () => isValid
  }))
  useEffect(() => {
    if(refErrorMessage.current && refErrorMessage.current.clientWidth < refErrorMessage.current.scrollWidth) {
      setOverflowed(true)
    } else {
      setOverflowed(false)
    }
  }, [isValid, errorMessage])

  return (
    <div className="ng-common-picker">
      <div className={classNames(
        "ng-common-picker__row",  
        { "ng-common-picker__row--choosen": value !== "" && value !== undefined && value !== " " }, 
        { "ng-common-picker__row--not-choosen": value === "" || value === undefined || value === " " },
        { "ng-common-picker__row--no-error": noError }
      )}>
        <If condition={value !== "" && value !== undefined && value !== " "}>
          <Then>
            {() => (
              <Fragment>
                <div className="ng-common-picker__row--choosen__top">
                  <When condition={value && !value.id}>
                    {() => (
                      <WithTooltip
                          data-place="top"
                          text={`To run ${tooltipElement} we take the newest version of this file.`}
                      >
                        <p className="ng-common-picker__row--choosen__top__latest">Take newest:</p>
                      </WithTooltip>
                    )}
                  </When>
                  <p className="ng-common-picker__row--choosen__top__name">{value?.name}</p>
                  <IconButton 
                      className={classNames(
                        "ng-common-picker__row--choosen__top__clear",
                        { "ng-common-picker__row--choosen__top__clear--is-disabled": disabled }
                      )}
                      onClick={clearValue}
                  >
                    <Clear/>
                  </IconButton>
                </div>
                <p className="ng-common-picker__row--choosen__optional">{"Group: " + (value?.groupName || "Unknown") + (withProvider && value?.provider ? `, Provider: ${value?.provider}` : '')}</p>
              </Fragment>
            )}
          </Then>
          <Else>
            {() => (
              <Button
                  disabled={disabled}
                  onClick={openModal}
                  size={size}
                  type="button"
                  variant="text"
              >
                {fileType ? `Select ${String(fileType).toLowerCase().capitaliseFirstLetter()}` : "Select file"}
              </Button>
            )}
          </Else>
        </If>
        <div 
            className={classNames("ng-common-picker__error-container", { "ng-common-picker__error-container--is-none": isValid })}
            data-testid="text-field-error-container"
        >
          <p ref={refErrorMessage}>
            {errorMessage}
          </p>
          <When condition={isOverflowed}>
            {() => PickersUtils.getErrorTooltip(errorMessage, "ng-common-picker__error-container__more")}
          </When>
        </div>
      </div>
      <When condition={Object.keys(tooltipProps).length > 0}>
        {() => (
          <div 
              className="ng-common-picker__tooltip-container"
              data-place="left"
              {...tooltipProps}
          >
            <Info/>
          </div>
        )}
      </When>
      <When condition={isOpen}>
        {() => (
          <Modal
              closeModal={closeModal}
              defaultValue={defaultValue}
              fileExtensions={fileExtensions}
              fileType={fileType}
              onSubmit={onSubmit}
              optionalText={optionalText}
              withProvider={withProvider}
              withVersion={withVersion}
          />
        )}
      </When>
    </div>
  )
}


FilePicker.propTypes = {
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  disabled: PropTypes.bool,
  fileExtensions: PropTypes.arrayOf(PropTypes.string),
  fileType: PropTypes.string,
  name: PropTypes.string.isRequired,
  noError: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  optionalText: PropTypes.string,
  required: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'normal', 'large']),
  tooltipElement: PropTypes.string,
  tooltipProps: PropTypes.object,
  validators: PropTypes.arrayOf(PropTypes.shape({
    message: PropTypes.string.isRequired,
    validator: PropTypes.func.isRequired
  })),
  withProvider: PropTypes.bool,
  withVersion: PropTypes.bool,
}

FilePicker.displayName = 'FilePicker';
